import React from 'react';

const SCENARIO_COLOR_MAP: Record<string, string> = {
  green: 'var(--color-scenario-green)',
  amber: 'var(--primary)',
  neutral: 'var(--foreground)',
};

/**
 * Simple markdown to HTML converter for tooltip content
 */
export const formatMarkdownToHtml = (markdown: string, variables?: Record<string, unknown>, enableFieldFocus?: boolean): string => {
  // Replace template variables if provided
  let processedMarkdown = markdown.replace(/\r\n/g, '\n');
  if (variables) {
    let previousMarkdown;
    // Iteratively process templates until no more changes are made, to handle nesting.
    do {
      previousMarkdown = processedMarkdown;

      // Handle each blocks FIRST to properly process conditionals within loops
      processedMarkdown = processedMarkdown.replace(/{{#each\s+(\w+)}}((?:(?!{{#each|{{\/each}})[\s\S])*?){{\/each}}/g, (match, arrayName, content) => {
        const arrayValue = variables[arrayName];
        if (Array.isArray(arrayValue)) {
          return arrayValue.map((item, index) => {
            let itemContent = content;
            
            // Handle simple conditional blocks with parent scope variables
            itemContent = itemContent.replace(/{{#if\s+\.\.\/(\w+)}}((?:(?!{{#if)[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (_match: string, condition: string, ifContent: string, elseContent: string) => {
              const conditionValue = variables[condition];
              return conditionValue ? ifContent : elseContent;
            });

            itemContent = itemContent.replace(/{{#if\s+\.\.\/(\w+)}}((?:(?!{{#if|{{else}})[\s\S])*?){{\/if}}/g, (_match: string, condition: string, content: string) => {
              const conditionValue = variables[condition];
              return conditionValue ? content : '';
            });

            // Handle conditionals within each blocks
            // {{#if (eq var 'value')}} - for item properties
            itemContent = itemContent.replace(/{{#if\s+\(eq\s+([a-zA-Z_]\w*)\s+'([^']+)'\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (_match: string, varName: string, value: string, ifContent: string, elseContent: string) => {
              const varValue = item[varName];
              return varValue === value ? ifContent : (elseContent || '');
            });
            itemContent = itemContent.replace(/{{#if\s+\(eq\s+([a-zA-Z_]\w*)\s+'([^']+)'\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{\/if}}/g, (_match: string, varName: string, value: string, content: string) => {
                const varValue = item[varName];
                return varValue === value ? content : '';
            });
            
            // {{#if (eq @index 0)}}
            itemContent = itemContent.replace(/{{#if\s+\(eq\s+@index\s+(\d+)\)}}((?:(?!{{#if|{{\/if}})[\s\S])*?){{\/if}}/g, (_match: string, indexValue: string, content: string) => {
              return index === parseInt(indexValue) ? content : '';
            });

            // Handle {{#if (and condition1 condition2)}} syntax
            itemContent = itemContent.replace(/{{#if\s+\(and\s+([^)]+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (match: string, _conditions: string, _ifContent: string, _elseContent: string) => {
              // This will be handled within the each block context
              return match; // Keep as-is for now, will be processed in each block
            });

            itemContent = itemContent.replace(/{{#if\s+\(and\s+([^)]+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{\/if}}/g, (match: string, _conditions: string, _content: string) => {
              // This will be handled within the each block context
              return match; // Keep as-is for now, will be processed in each block
            });

            // Handle {{#if (gte @index value)}} syntax
            itemContent = itemContent.replace(/{{#if\s+\(gte\s+@index\s+([^)]+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (match: string, _value: string, _ifContent: string, _elseContent: string) => {
              // This will be handled within the each block context
              return match; // Keep as-is for now, will be processed in each block
            });

            itemContent = itemContent.replace(/{{#if\s+\(gte\s+@index\s+([^)]+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{\/if}}/g, (match: string, _value: string, _content: string) => {
              // This will be handled within the each block context
              return match; // Keep as-is for now, will be processed in each block
            });

            // {{#if (and ../useAdditionalOffset (gte @index ../escalationCadenceCount))}}
            itemContent = itemContent.replace(/{{#if\s+\(and\s+\.\.\/(\w+)\s+\(gte\s+@index\s+\.\.\/(\w+)\)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (_match: string, condition1: string, condition2: string, ifContent: string, elseContent: string) => {
              const cond1Value = variables[condition1];
              const cond2Value = Number(variables[condition2]) || 0;
              const result = cond1Value && index >= cond2Value;
              return result ? ifContent : elseContent;
            });

            // {{#if (and (eq @index 0) ../useAdditionalOffset)}}
            itemContent = itemContent.replace(/{{#if\s+\(and\s+\(eq\s+@index\s+(\d+)\)\s+\.\.\/(\w+)\)}}((?:(?!{{#if|{{else}}|{{\/if}})[\s\S])*?){{\/if}}/g, (_match: string, indexValue: string, condition: string, content: string) => {
              const conditionValue = variables[condition];
              const result = index === parseInt(indexValue) && conditionValue;
              return result ? content : '';
            });

            // Handle {{#if (and (eq @index 0) (gt|lt ../varName value))}}
            itemContent = itemContent.replace(/{{#if\s+\(and\s+\(eq\s+@index\s+(\d+)\)\s+\((gt|lt)\s+\.\.\/(\w+)\s+(\d+)\)\)}}((?:(?!{{#if|{{else}}|{{\/if}})[\s\S])*?){{\/if}}/g, (_match: string, indexValue: string, op: string, varName: string, value: string, content: string) => {
                const varValue = variables[varName];
                const numValue = parseInt(value, 10);
                if (index !== parseInt(indexValue, 10) || typeof varValue !== 'number') return '';
                const result = op === 'gt' ? varValue > numValue : varValue < numValue;
                return result ? content : '';
            });

            // Handle simple {{#if (gt|lt ../varName value)}}
            itemContent = itemContent.replace(/{{#if\s+\((gt|lt)\s+\.\.\/(\w+)\s+(\d+)\)}}((?:(?!{{#if|{{else}}|{{\/if}})[\s\S])*?){{\/if}}/g, (_match: string, op: string, varName: string, value: string, content: string) => {
                const varValue = variables[varName];
                const numValue = parseInt(value, 10);
                if (typeof varValue !== 'number') return '';
                const result = op === 'gt' ? varValue > numValue : varValue < numValue;
                return result ? content : '';
            });

            // Handle simple {{#if (gt itemVar value)}}
            itemContent = itemContent.replace(/{{#if\s+\(gt\s+([a-zA-Z_]\w*)\s+(\d+)\)}}((?:(?!{{#if|{{else}}|{{\/if}})[\s\S])*?){{\/if}}/g, (_match: string, varName: string, gtValue: string, content: string) => {
                const varValue = item[varName];
                const result = typeof varValue === 'number' && varValue > parseInt(gtValue, 10);
                return result ? content : '';
            });

            // Replace item and parent variables (e.g., {{ordinal}}, {{../gracePeriodMinutesPadded}})
            itemContent = itemContent.replace(/{{{\s*([\w./]+)\s*}}}|{{\s*([\w./]+)\s*}}/g, (_match: string, path1: string, path2: string) => {
                const path = path1 || path2;
                let value: unknown;
                if (path.startsWith('../')) {
                    const varPath = path.substring(3);
                    const keys = varPath.split('.');
                    value = variables as Record<string, unknown>;
                    for (const key of keys) {
                        value = (value as Record<string, unknown>)?.[key];
                        if (value === undefined) break;
                    }
                } else {
                    const keys = path.split('.');
                    value = item;
                    for (const key of keys) {
                        value = (value as Record<string, unknown>)?.[key];
                        if (value === undefined) break;
                    }
                }
                return value !== undefined ? String(value) : '';
            });

            return itemContent;
          }).join('');
        }
        return '';
      });

      // Handle conditional blocks with function calls like {{#if (eq var 'value')}}
      processedMarkdown = processedMarkdown.replace(/{{#if\s+\(eq\s+(\w+)\s+'([^']+)'\)}}((?:(?!{{#if)[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (match, varName, value, ifContent, elseContent) => {
        const varValue = variables[varName];
        return varValue === value ? ifContent : elseContent;
      });
      
      processedMarkdown = processedMarkdown.replace(/{{#if\s+\(eq\s+(\w+)\s+'([^']+)'\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{\/if}}/g, (match, varName, value, content) => {
        const varValue = variables[varName];
        return varValue === value ? content : '';
      });

      // Handle conditional blocks with gt (greater than) function calls like {{#if (gt var value)}}
      processedMarkdown = processedMarkdown.replace(/{{#if\s+\(gt\s+(\w+)\s+(\d+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (match, varName, value, ifContent, elseContent) => {
        const varValue = variables[varName];
        const numValue = parseInt(value);
        return (typeof varValue === 'number' && varValue > numValue) ? ifContent : elseContent;
      });
      
      processedMarkdown = processedMarkdown.replace(/{{#if\s+\(gt\s+(\w+)\s+(\d+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{\/if}}/g, (match, varName, value, content) => {
        const varValue = variables[varName];
        const numValue = parseInt(value);
        return (typeof varValue === 'number' && varValue > numValue) ? content : '';
      });

      // Handle more complex conditionals like {{#if (eq @index 0)}}
      processedMarkdown = processedMarkdown.replace(/{{#if\s+\(eq\s+@index\s+(\d+)\)}}((?:(?!{{#if|{{\/if}})[\s\S])*?){{\/if}}/g, (match, indexValue, content) => {
        // This will be handled within the each block context
        return match; // Keep as-is for now, will be processed in each block
      });

      // Handle {{#if (and condition1 condition2)}} syntax
      processedMarkdown = processedMarkdown.replace(/{{#if\s+\(and\s+([^)]+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (match, conditions, ifContent, elseContent) => {
        // This will be handled within the each block context
        return match; // Keep as-is for now, will be processed in each block
      });

      processedMarkdown = processedMarkdown.replace(/{{#if\s+\(and\s+([^)]+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{\/if}}/g, (match, conditions, content) => {
        // This will be handled within the each block context
        return match; // Keep as-is for now, will be processed in each block
      });

      // Handle {{#if (gte @index value)}} syntax
      processedMarkdown = processedMarkdown.replace(/{{#if\s+\(gte\s+@index\s+([^)]+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (match, value, ifContent, elseContent) => {
        // This will be handled within the each block context
        return match; // Keep as-is for now, will be processed in each block
      });

      processedMarkdown = processedMarkdown.replace(/{{#if\s+\(gte\s+@index\s+([^)]+)\)}}((?:(?!{{#if|{{else}})[\s\S])*?){{\/if}}/g, (match, value, content) => {
        // This will be handled within the each block context
        return match; // Keep as-is for now, will be processed in each block
      });
      
      // Handle simple conditional blocks
      processedMarkdown = processedMarkdown.replace(/{{#if\s+(\w+)}}((?:(?!{{#if)[\s\S])*?){{else}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g, (match, condition, ifContent, elseContent) => {
        const conditionValue = variables[condition];
        return conditionValue ? ifContent : elseContent;
      });
      
      // Handle simple if blocks without else
      processedMarkdown = processedMarkdown.replace(/{{#if\s+(\w+)}}((?:(?!{{#if|{{else}})[\s\S])*?){{\/if}}/g, (match, condition, content) => {
        const conditionValue = variables[condition];
        return conditionValue ? content : '';
      });
    } while (processedMarkdown !== previousMarkdown);
    
    // Handle simple expressions like {{Math.ceil(45 / (creditIntervalMinutes || 15))}}
    processedMarkdown = processedMarkdown.replace(/{{{\s*([\s\S]+?)\s*}}}|{{\s*([\s\S]+?)\s*}}/g, (match, expr1, expr2) => {
      const expression = (expr1 || expr2).trim();
      try {
        // Prevent evaluation of template directives that should have already been processed
        if (expression.startsWith("#") || expression.startsWith("/")) {
          return "";
        }
        // Skip if this looks like a template directive that wasn't processed
        if (expression.includes('#if') || expression.includes('#each') || expression.includes('/if') || expression.includes('/each')) {
          return ''; // Remove unprocessed template directives
        }
        
        // Create a safe evaluation context with the variables
        const func = new Function(...Object.keys(variables || {}), `return ${expression}`);
        const result = func(...Object.values(variables || {}));
        return String(result);
      } catch (error) {
        // If expression evaluation fails, try simple variable replacement
        const simpleVarMatch = expression.match(/^(\w+)$/);
        if (simpleVarMatch && variables && variables[simpleVarMatch[1]] !== undefined) {
          return String(variables[simpleVarMatch[1]]);
        }
        // Remove unprocessed template code instead of returning it
        return '';
      }
    });
    
    // Final cleanup: remove any remaining template tags that weren't processed
    processedMarkdown = processedMarkdown.replace(/{{[^}]*}}/g, '');
  }
  // First, handle headers before any other processing
  let html = processedMarkdown
    // Headers (process from most specific to least specific)
    .replace(/^###### (.*$)/gm, '<h6 class="text-xs font-semibold text-muted-foreground mb-1 mt-2">$1</h6>')
    .replace(/^##### (.*$)/gm, '<h5 class="text-xs font-bold text-primary-text mb-2 mt-3">$1</h5>')
    .replace(/^#### (.*$)/gm, '<h4 class="text-sm font-bold text-primary-text mb-2 mt-1">$1</h4>')
    .replace(/^### (.*$)/gm, '<h3 class="text-base leading-none font-bold text-primary-text mt-2 mb-2">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="text-lg font-bold text-primary-text mb-3 mt-4">$1</h2>')
    .replace(/^# (.*$)/gm, '<h1 class="text-xl font-bold text-primary-text mb-4 mt-4">$1</h1>');
  
  // Fork diagram directive — side-by-side outcome cards
  html = html.replace(
    /\[\[FORK\]\]\s*([\s\S]*?)\s*\[\[\/FORK\]\]/g,
    (match, content) => {
      const rows = content.trim().split(/\n/).filter((line: string) => line.trim());
      const cards = rows.map((row: string) => {
        const parts = row.split('|').map((p: string) => p.trim());
        if (parts.length < 2) return '';
        const [label, description, color] = parts;
        const borderColor = color && SCENARIO_COLOR_MAP[color] ? SCENARIO_COLOR_MAP[color] : 'var(--border)';
        return `<div class="fork-card" style="border-color:${borderColor}">
        <div class="fork-label" style="color:${borderColor}">${label}</div>
        <div class="fork-description">${description}</div>
      </div>`;
      }).join('');
      return `<div class="fork-container">${cards}</div>`;
    }
  );

  // Scenario cards directive — stacked visual example rows
  // Supports three formats:
  //   5-part: worked | operator | threshold | billed | color  (fixed sublabels)
  //   4-part: worked | billed | color | explanation
  //   flow (~): value~label | value~label | value~label | color  (custom sublabels)
  html = html.replace(
    /\[\[SCENARIO_(?:CARDS|FLOW)\]\]\s*([\s\S]*?)\s*\[\[\/SCENARIO_(?:CARDS|FLOW)\]\]/g,
    (match, content) => {
      const rows = content.trim().split(/\n/).filter((line: string) => line.trim());
      const cards = rows.map((row: string) => {
        const parts = row.split('|').map((p: string) => p.trim());
        // 5-part format: worked | operator | threshold | billed | color [| explanation]
        // Supports value~label syntax for custom sublabels (e.g., "8h~worked")
        if (parts.length >= 5 && !parts[1].includes('~') && SCENARIO_COLOR_MAP[parts[4].trim()]) {
          const [rawWorked, operator, rawThreshold, rawBilled, color, ...explanationParts] = parts;
          const parseCell = (cell: string, defaultLabel: string) => {
            if (cell.includes('~')) {
              const [value, label] = cell.split('~').map((s: string) => s.trim());
              return { value, label };
            }
            return { value: cell, label: defaultLabel };
          };
          const worked = parseCell(rawWorked, 'Worked');
          const threshold = parseCell(rawThreshold, 'Threshold');
          const billed = parseCell(rawBilled, 'Billed');
          const billedColor = SCENARIO_COLOR_MAP[color] || SCENARIO_COLOR_MAP.neutral;
          const explanation = explanationParts.length > 0 ? explanationParts.join('|').trim() : '';
          let cardHtml = `<div class="scenario-card">
          <div class="scenario-worked">${worked.value}<span class="scenario-sublabel">${worked.label}</span></div>
          <div class="scenario-operator">${operator}</div>
          <div class="scenario-worked">${threshold.value}<span class="scenario-sublabel">${threshold.label}</span></div>
          <div class="scenario-arrow">→</div>
          <div class="scenario-billed" style="color:${billedColor}">${billed.value}<span class="scenario-sublabel">${billed.label}</span></div>
        </div>`;
          if (explanation) {
            cardHtml += `<div class="scenario-caption" style="color:${billedColor}">${explanation}</div>`;
          }
          return cardHtml;
        }
        if (parts.length < 3) return '';
        // Flow format (~ separator): value~label | [operator] | value~label | color
        // Parts without ~ are treated as operators between steps (rendered instead of →)
        if (parts[0].includes('~')) {
          const lastPart = parts[parts.length - 1].trim();
          let color: string;
          let flowCaption = '';
          let rawParts: string[];
          if (SCENARIO_COLOR_MAP[lastPart]) {
            color = lastPart;
            rawParts = parts.slice(0, -1);
          } else if (parts.length >= 3 && SCENARIO_COLOR_MAP[parts[parts.length - 2].trim()]) {
            color = parts[parts.length - 2].trim();
            flowCaption = lastPart;
            rawParts = parts.slice(0, -2);
          } else {
            color = lastPart;
            rawParts = parts.slice(0, -1);
          }
          // Separate value~label steps from plain operator parts
          const items: Array<{ type: 'step'; value: string; label: string } | { type: 'op'; symbol: string }> = [];
          for (const p of rawParts) {
            if (p.includes('~')) {
              const [value, label] = p.split('~').map((s: string) => s.trim());
              items.push({ type: 'step', value, label: label || '' });
            } else {
              items.push({ type: 'op', symbol: p });
            }
          }
          const resultColor = SCENARIO_COLOR_MAP[color] || SCENARIO_COLOR_MAP.neutral;
          const htmlParts = items.map((item, i) => {
            if (item.type === 'op') {
              return `<div class="scenario-operator">${item.symbol}</div>`;
            }
            const isLast = !items.slice(i + 1).some(x => x.type === 'step');
            const cls = isLast ? 'scenario-billed' : 'scenario-worked';
            const style = isLast ? ` style="color:${resultColor}"` : '';
            // Insert → arrow before this step if the previous item was also a step (no operator between)
            const prev = i > 0 ? items[i - 1] : null;
            const arrow = prev && prev.type === 'step' ? '<div class="scenario-arrow">→</div>' : '';
            return `${arrow}<div class="${cls}"${style}>${item.value}<span class="scenario-sublabel">${item.label}</span></div>`;
          }).join('\n          ');
          let flowHtml = `<div class="scenario-card">
          ${htmlParts}
        </div>`;
          if (flowCaption) {
            flowHtml += `<div class="scenario-caption" style="color:${resultColor}">${flowCaption}</div>`;
          }
          return flowHtml;
        }
        // 4-part format: worked | billed | color | explanation
        const [worked, billed, color, ...explanationParts] = parts;
        const explanation = explanationParts.join('|');
        const billedColor = SCENARIO_COLOR_MAP[color] || SCENARIO_COLOR_MAP.neutral;
        return `<div class="scenario-card">
        <div class="scenario-worked">${worked}<span class="scenario-sublabel">worked</span></div>
        <div class="scenario-arrow">→</div>
        <div class="scenario-billed" style="color:${billedColor}">${billed}<span class="scenario-sublabel">billed</span></div>
        <div class="scenario-explanation">${explanation}</div>
      </div>`;
      }).join('');
      return `<div class="scenario-cards-container">${cards}</div>`;
    }
  );

  // Callout directives — label on its own line, content indented below
  for (const [tag, label] of [['NOTE', 'Note:'], ['SUGGESTION', 'Suggestion:'], ['TIP', 'Tip:']] as const) {
    html = html.replace(
      new RegExp(`\\[\\[${tag}\\]\\]\\s*([\\s\\S]*?)\\s*\\[\\[\\/${tag}\\]\\]`, 'g'),
      (_match, content) =>
        `<div class="note-callout"><div class="note-label">${label}</div><div class="note-content">${content.trim()}</div></div>`
    );
  }

  // Then, handle tables - always convert to HTML table elements
  html = html.replace(/(\|.*\|[\r\n]+)+/g, (match) => {
    const lines = match.trim().split(/[\r\n]+/);
    if (lines.length < 2) return match;
    
    const headerRow = lines[0];
    const separatorRow = lines[1];
    const dataRows = lines.slice(2);
    
    // Check if it's a valid table (separator row should contain dashes)
    if (!separatorRow.includes('-')) return match;
    
    // Parse header
    const headerCells = headerRow.split('|').map(h => h.trim());
    // Remove first and last empty cells (from leading/trailing |)
    if (headerCells.length > 0 && headerCells[0] === '') headerCells.shift();
    if (headerCells.length > 0 && headerCells[headerCells.length - 1] === '') headerCells.pop();
    const headers = headerCells;
    
    // Parse data rows
    const rows = dataRows.map(row => {
      const cells = row.split('|').map(cell => cell.trim());
      // Remove first and last empty cells (from leading/trailing |)
      if (cells.length > 0 && cells[0] === '') cells.shift();
      if (cells.length > 0 && cells[cells.length - 1] === '') cells.pop();
      return cells;
    });
    
    // Check if this is the specific "Single Meal Mode vs Multiple Meal Mode" table
    const isComparisonTable = headers.length === 2 && 
      headers[0].includes('Single Meal Mode') && 
      headers[1].includes('Multiple Meal Mode');
    
    // Check if this is the "How It Works" table with Billable Hours/Recorded Hours columns
    const isHowItWorksTable = headers.length === 3 && 
      headers[1].includes('Billable Hours') && 
      headers[2].includes('Recorded Hours');
    
    // Always use HTML table elements with consistent styling
    const tableClass = isComparisonTable 
      ? 'table-auto border-collapse border border-border/50 text-xs w-full my-3 text-center'
      : isHowItWorksTable
      ? 'table-auto border-collapse border border-border/50 text-xs w-full mb-1'
      : 'table-auto border-collapse border border-border/50 text-xs w-full mb-3';
    
    let tableHtml = '';
    
    // Add switch above comparison table but below any title
    if (isComparisonTable) {
      tableHtml += `<div class="flex justify-center items-center mb-2">`;
      tableHtml += `<INJECT:multiple-meal-break-switch/>`;
      tableHtml += `</div>`;
    }
    
    tableHtml += `<table class="${tableClass}"><thead><tr class="border-b border-border/50">`;
    
    // Header
    if (isComparisonTable) {
        headers.forEach(header => {
            tableHtml += `<th class="px-2 py-1 font-semibold text-center align-middle">${header}</th>`;
        });
    } else if (isHowItWorksTable) {
        headers.forEach((header, index) => {
            const headerClass = index === 0 
              ? 'px-2 py-1 bg-muted/30 font-semibold text-center w-auto align-middle'
              : 'px-2 py-1 bg-muted/30 font-semibold text-center w-1/2 align-middle';
            tableHtml += `<th class="${headerClass}">${header}</th>`;
        });
    } else {
        headers.forEach(header => {
            tableHtml += `<th class="px-2 py-1 bg-muted/30 font-semibold text-center align-middle">${header}</th>`;
        });
    }
    
    tableHtml += '</tr></thead><tbody>';
    
    // Body
    rows.forEach((row, rowIndex) => {
      const isLastRow = rowIndex === rows.length - 1;
      const trClass = isLastRow ? '' : 'border-b border-border/50';
      tableHtml += `<tr class="${trClass}">`;
      // Ensure we have the same number of cells as headers
      for (let i = 0; i < headers.length; i++) {
        const cell = row[i] || ''; // Use empty string if cell doesn't exist
        const cellClass = isComparisonTable
          ? 'px-2 py-1 align-middle'
          : isHowItWorksTable && i === 0
          ? 'px-2 py-1 w-auto align-middle'
          : isHowItWorksTable && i > 0
          ? 'px-2 py-1 w-1/2 align-middle'
          : 'px-2 py-1 align-middle text-center';
        tableHtml += `<td class="${cellClass}">${cell}</td>`;
      }
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody></table>';

    // Wrap in horizontal scroll container for mobile
    return `<div class="overflow-x-auto -mx-1 px-1">${tableHtml}</div>`;
  });
  
  return html
    // Horizontal rule
    .replace(/^-{3,}/gm, '<hr class="my-3 border-border/50" />')
    // Column layout (process structural elements after headers)
    // Process column directives while preserving line structure
    .replace(/\[\[COL_START(?::(\d+))?(?::(center|top|bottom)?)?(?::gap-(\d+))?\]\]/g, (match, cols, alignment, gap) => {
      const numCols = parseInt(cols || '2', 10);
      let gridClass;
      switch (numCols) {
        case 1: gridClass = 'grid-cols-1'; break;
        case 3: gridClass = 'grid-cols-3'; break;
        case 4: gridClass = 'grid-cols-4'; break;
        default: gridClass = 'grid-cols-2';
      }
      
      let alignmentClass = '';
      switch (alignment) {
        case 'center': alignmentClass = 'items-center'; break;
        case 'top': alignmentClass = 'items-start'; break;
        case 'bottom': alignmentClass = 'items-end'; break;
        default: alignmentClass = 'items-start'; // default alignment
      }
      
      const gapClass = gap ? `gap-x-${gap}` : 'gap-x-3';
      
      return `<div class="grid ${gridClass} ${alignmentClass} ${gapClass} gap-y-2 mb-2"><div class="space-y-2">`;
    })
    .replace(/\[\[COL_BREAK(?::(center|top|bottom))?\]\]/g, (match, alignment) => {
      let alignmentClass = '';
      switch (alignment) {
        case 'center': alignmentClass = ' flex items-center'; break;
        case 'top': alignmentClass = ' flex items-start'; break;
        case 'bottom': alignmentClass = ' flex items-end'; break;
        default: alignmentClass = '';
      }
      return `</div><div class="space-y-2${alignmentClass}">`;
    })
    .replace(/\[\[COL_END\]\]/g, '</div></div>')
    // Bold text (must come before italic to avoid conflicts)
    .replace(/\*\*([^*]+?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>')
    // Italic text
    .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')
    // Inline references — field names and formulas styled as warm semibold labels
    .replace(/`([^`]+)`/g, (match, content) => {
      if (enableFieldFocus) {
        const fieldMap: Record<string, string> = {
          'Multiple Meal Breaks': 'recurring-meal-window',
          'Credit Interval': 'surcharge-cadence-credit',
          'Additional Offset': 'use-escalation',
          'Step-Up Deadline': 'escalation-threshold-minutes',
          'Time Credit': 'credit-extra-time-minutes',
          'Meal Break Deadline': 'meal-window-threshold',
          'Meal Break Length': 'meal-break-length',
          'Grace Period': 'grace-period'
        };

        const fieldId = fieldMap[content];
        if (fieldId) {
          return `<button type="button" class="field-ref field-ref--link" onclick="window.focusField && window.focusField('${fieldId}')">${content}</button>`;
        }
      }
      return `<button type="button" class="field-ref field-ref--nav" onclick="window.navigateHelp && window.navigateHelp('${content}')">${content}</button>`;
    })
    // Numbered lists
    // Bullet lists
    .replace(/^[*-]\s+(.*)$/gm, '<div class="flex gap-2 mb-1"><span class="text-primary-text font-medium min-w-[1.2em] text-center shrink-0">&#8226;</span><span>$1</span></div>')
    .replace(/^(\d+)\.\s+(.*)$/gm, '<div class="flex gap-2 mb-1"><span class="text-primary-text font-medium min-w-[1.2em]">$1.</span><span>$2</span></div>')
    // Line breaks
    .replace(/\n\n/g, '<div class="mb-3"></div>')
    .replace(/\n/g, '<br/>')
    // Cleanup for columns, just in case a stray newline was converted
    .replace(/<\/div><div class="mb-3"><\/div><div>/g, '</div><div>')
    // Avoid trailing spacer-only dead space at end of markdown blocks.
    .replace(/(?:<div class="mb-4"><\/div>\s*)+$/g, '');
};

/**
 * Hook to inject custom CSS styles for formatted markdown content.
 * Uses a singleton style element to avoid duplicates across multiple mounts.
 */
let styleRefCount = 0;
let sharedStyleEl: HTMLStyleElement | null = null;

export const useMarkdownStyles = (): void => {
  React.useEffect(() => {
    if (sharedStyleEl) {
      styleRefCount++;
      return () => {
        styleRefCount--;
        if (styleRefCount === 0 && sharedStyleEl) {
          document.head.removeChild(sharedStyleEl);
          sharedStyleEl = null;
        }
      };
    }
    const style = document.createElement('style');
    style.textContent = `
      .formatted-markdown h4 {
        color: var(--primary);
        font-weight: 600;
        margin-bottom: 0.5rem;
        margin-top: 0.25rem;
      }
      .formatted-markdown h5 {
        color: var(--primary);
        font-weight: 600;
        margin-bottom: 0.5rem;
        margin-top: 0.75rem;
      }
      .formatted-markdown strong {
        color: var(--foreground);
        font-weight: 600;
      }
      .formatted-markdown .field-ref {
        font-family: inherit;
        font-weight: 600;
        font-size: inherit;
        color: oklch(from var(--primary) l c h / 0.85);
        white-space: nowrap;
        /* Reset button styles for inline appearance */
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        display: inline;
        line-height: inherit;
        text-align: inherit;
        letter-spacing: inherit;
      }
      .formatted-markdown .field-ref--nav,
      .formatted-markdown .field-ref--link {
        cursor: pointer;
        transition: color 0.15s;
      }
      .formatted-markdown .field-ref--nav:hover,
      .formatted-markdown .field-ref--link:hover {
        color: var(--primary);
      }
      .formatted-markdown .field-ref:focus-visible {
        outline: 2px solid oklch(from var(--primary) l c h / 0.5);
        outline-offset: 2px;
        border-radius: 2px;
      }
      .formatted-markdown .formatted-markdown-part > .flex:not([class*="items-"]) {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 0.125rem;
        align-items: flex-start;
      }
      .formatted-markdown .formatted-markdown-part > .flex:not([class*="items-"]):not([class*="justify-"]) > span:first-child {
        color: var(--primary);
        font-weight: 500;
        min-width: 1.2em;
        flex-shrink: 0;
      }
      .formatted-markdown table {
        border-collapse: collapse;
        width: 100%;
        margin: 0.75rem 0;
        font-size: 0.75rem;
      }
      .formatted-markdown th {
        border: 1px solid oklch(from var(--border) l c h / 0.5);
        padding: 0.5rem;
        background-color: oklch(from var(--muted) l c h / 0.5);
        font-weight: 600;
        text-align: left;
      }
      .formatted-markdown td {
        border: 1px solid oklch(from var(--border) l c h / 0.5);
        padding: 0.5rem;
      }
      .formatted-markdown tbody tr.logical-odd {
        background-color: oklch(from var(--muted) l c h / 0.15);
      }
      .formatted-markdown tbody tr.logical-even {
        background-color: oklch(from var(--muted) l c h / 0.25);
      }
      .formatted-markdown table.text-center {
        text-align: center;
      }
      .formatted-markdown table.text-center th,
      .formatted-markdown table.text-center td {
        border: none;
        text-align: center;
        background-color: transparent;
      }
      .formatted-markdown .fork-container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.75rem;
        margin: 0.75rem 0;
      }
      .formatted-markdown .fork-card {
        border: 1.5px solid;
        border-radius: 0.25rem;
        padding: 0.875rem;
        text-align: center;
        background: oklch(from var(--background) calc(l - 0.01) c h);
      }
      .formatted-markdown .fork-label {
        font-weight: 700;
        font-size: 1rem;
        font-family: var(--font-mono, 'JetBrains Mono', monospace);
        margin-bottom: 0.375rem;
      }
      .formatted-markdown .fork-description {
        font-size: 0.8125rem;
        color: oklch(from var(--muted-foreground) l c h);
      }
      .formatted-markdown .scenario-cards-container {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin: 0.75rem 0 0.25rem;
      }
      .formatted-markdown .scenario-card > br {
        display: none;
      }
      .formatted-markdown .scenario-card {
        display: grid;
        grid-template-columns: 1fr 1.5rem 1fr 1.5rem 1fr;
        align-items: center;
        gap: 0.375rem;
        padding: 0.75rem 0.5rem;
        background: oklch(from var(--background) l c h / 0.8);
        border-radius: 0.25rem;
        border: 1px solid oklch(from var(--border) l c h / 0.5);
      }
      .formatted-markdown .scenario-worked,
      .formatted-markdown .scenario-billed {
        min-width: 0;
        text-align: center;
        font-size: 1.125rem;
        font-weight: 700;
        font-family: 'JetBrains Mono', monospace;
        color: var(--foreground);
        line-height: 1;
      }
      .formatted-markdown .scenario-sublabel {
        display: block;
        font-size: 0.6875rem;
        font-weight: 400;
        color: var(--muted-foreground);
        margin-top: 0.25rem;
      }
      .formatted-markdown .scenario-arrow {
        font-size: 1rem;
        color: oklch(from var(--muted-foreground) l c h / 0.75);
        padding-bottom: 0.25rem;
        text-align: center;
      }
      .formatted-markdown .scenario-operator {
        font-size: 1.125rem;
        font-weight: 600;
        color: oklch(from var(--muted-foreground) l c h / 0.75);
        padding-bottom: 0.25rem;
        text-align: center;
      }
      .formatted-markdown .scenario-explanation {
        font-size: 0.75rem;
        color: oklch(from var(--popover-foreground) l c h / 0.92);
        grid-column: 4 / -1;
        padding-bottom: 0.25rem;
      }
      .formatted-markdown .scenario-caption {
        font-size: 0.75rem;
        opacity: 1;
        text-align: center;
        margin-top: -0.25rem;
        margin-bottom: 0.125rem;
      }
      .formatted-markdown details {
        background: oklch(from var(--background) calc(l - 0.02) c h);
        border: 1px solid oklch(from var(--border) l c h / 0.5);
        border-radius: 0.25rem;
        margin: 0.75rem 0;
      }
      .formatted-markdown details summary {
        padding: 0.625rem 0.875rem;
        font-size: 0.75rem;
        color: var(--primary);
        font-weight: 500;
        cursor: pointer;
        list-style: none;
        display: flex;
        align-items: center;
        gap: 0.375rem;
      }
      .formatted-markdown details summary::-webkit-details-marker {
        display: none;
      }
      .formatted-markdown details summary::before {
        content: '▸';
        font-size: 0.625rem;
        transition: transform 0.2s;
      }
      .formatted-markdown details[open] summary::before {
        transform: rotate(90deg);
      }
      .formatted-markdown details > div,
      .formatted-markdown details > .scenario-cards-container {
        padding: 0 0.875rem 0.875rem;
      }
      .formatted-markdown {
        padding-top: 0.5rem;
      }
      .formatted-markdown > *:last-child {
        margin-bottom: 0;
      }
      .formatted-markdown .note-callout {
        margin: 0.75rem 0 0;
      }
      .formatted-markdown .note-label {
        font-weight: 600;
        color: var(--foreground);
      }
      .formatted-markdown .note-content {
        padding-left: 0.75rem;
        opacity: 0.7;
      }
    `;
    document.head.appendChild(style);
    sharedStyleEl = style;
    styleRefCount++;

    return () => {
      styleRefCount--;
      if (styleRefCount === 0 && sharedStyleEl) {
        document.head.removeChild(sharedStyleEl);
        sharedStyleEl = null;
      }
    };
  }, []);
};

/**
 * Component for rendering formatted markdown content in tooltips
 */
interface FormattedMarkdownProps {
  content: string;
  className?: string;
  variables?: Record<string, unknown>;
  enableFieldFocus?: boolean;
  injectableComponents?: Record<string, React.ReactNode>;
  measureGranularity?: 'coarse' | 'fine' | 'micro' | 'dom';
  enableMeasureTextTargets?: boolean;
  /**
   * Live `<RateLadderOffsetGraphic>` (or any React node) substituted in for
   * `[RateLadderOffsetGraphic]` placeholders inside the markdown body. When
   * omitted, the placeholder is stripped so body copy renders cleanly.
   */
  rateLadderGraphic?: React.ReactNode;
}

function getTokenLabel(text: string): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (!collapsed) return 'Markdown Token';
  return collapsed.length > 48 ? `Markdown Token: ${collapsed.slice(0, 48)}…` : `Markdown Token: ${collapsed}`;
}

function instrumentMarkdownTextTokens(html: string): string {
  if (!html || typeof DOMParser === 'undefined' || typeof NodeFilter === 'undefined') {
    return html;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return html;

    const skipParentTags = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION']);
    const textNodes: Text[] = [];
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      const textNode = node as Text;
      const parent = textNode.parentElement;
      const value = textNode.nodeValue ?? '';
      const hasText = /\S/.test(value);
      const shouldSkip = !parent
        || skipParentTags.has(parent.tagName)
        || parent.classList.contains('markdown-measure-token')
        || parent.closest('[data-no-measure-token]') !== null;
      if (hasText && !shouldSkip) {
        textNodes.push(textNode);
      }
      node = walker.nextNode();
    }

    textNodes.forEach((textNode) => {
      const value = textNode.nodeValue ?? '';
      const parts = value.split(/(\s+)/).filter((part) => part.length > 0);
      if (parts.length === 0) return;

      const fragment = doc.createDocumentFragment();
      parts.forEach((part) => {
        if (/^\s+$/.test(part)) {
          fragment.appendChild(doc.createTextNode(part));
          return;
        }
        const token = doc.createElement('span');
        token.className = 'markdown-measure-token';
        token.setAttribute('data-measure-token', 'markdown-word');
        token.setAttribute('data-measure-label', getTokenLabel(part));
        token.textContent = part;
        fragment.appendChild(token);
      });
      textNode.parentNode?.replaceChild(fragment, textNode);
    });

    return root.innerHTML;
  } catch {
    return html;
  }
}

const DEFAULT_INJECTABLE_TOP_SPACING_CLASS = '';

const INJECTABLE_TOP_SPACING_CLASS_BY_KEY: Record<string, string> = {
  // Table switch sits in its own row with built-in spacing.
  'multiple-meal-break-switch': '',
  // Timelines/comparisons that previously compensated with negative margins.
  shift_timeline: '',
  rest_gap: '',
  surcharge_scope: '',
  fixed_premium_breakdown: '',
  premium_breakdown: '',
  ladder_comparison: '',
};

function getInjectableTopSpacingClass(injectedPart: string): string {
  return INJECTABLE_TOP_SPACING_CLASS_BY_KEY[injectedPart] ?? DEFAULT_INJECTABLE_TOP_SPACING_CLASS;
}

export const FormattedMarkdown: React.FC<FormattedMarkdownProps> = ({
  content,
  className = "",
  variables,
  enableFieldFocus = false,
  injectableComponents,
  measureGranularity = 'coarse',
  enableMeasureTextTargets = false,
  rateLadderGraphic,
}) => {
  useMarkdownStyles();

  const hasMeaningfulHtml = React.useCallback((html: string): boolean => {
    if (!html) return false;
    const textOnly = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
    if (textOnly.length > 0) return true;
    return /<(table|hr|img|svg|canvas|details|ul|ol|li|blockquote|pre|code|iframe)\b/i.test(html);
  }, []);

  // Expose focusField to window for onclick handlers
  React.useEffect(() => {
    if (enableFieldFocus && typeof window !== 'undefined') {
      (window as unknown as Record<string, unknown>).focusField = (fieldId: string) => {
        import('@/utils/focus').then(({ focusField }) => {
          focusField(fieldId);
        });
      };
    }
  }, [enableFieldFocus]);

  // Rewrite `[RateLadderOffsetGraphic]` placeholder into the standard injectable
  // token so it flows through the existing injection pipeline. When no live
  // graphic is provided we strip the placeholder to avoid leaving stray text.
  const RATE_LADDER_PLACEHOLDER = /\[RateLadderOffsetGraphic\]/g;
  const preparedBody = rateLadderGraphic
    ? content.replace(RATE_LADDER_PLACEHOLDER, '<INJECT:rate_ladder_offset_graphic/>')
    : content.replace(RATE_LADDER_PLACEHOLDER, '');

  const mergedInjectableComponents: Record<string, React.ReactNode> | undefined = rateLadderGraphic
    ? { ...(injectableComponents ?? {}), rate_ladder_offset_graphic: rateLadderGraphic }
    : injectableComponents;

  const html = formatMarkdownToHtml(preparedBody, variables, enableFieldFocus);
  const parts = [html]; // Treat the entire content as a single part for robust layout
  const instrumentTextTargets = enableMeasureTextTargets
    && (measureGranularity === 'micro' || measureGranularity === 'dom');

  return React.createElement(
    'div',
    { className: `max-w-none ${className}` },
    React.createElement(
      'div',
      { className: 'leading-relaxed text-popover-foreground/90' },
      parts.map((part, index) => {
        if (!part.trim()) return null; // Ignore empty parts from the split

        const containerClassName = 'formatted-markdown w-full';

        if (mergedInjectableComponents && part.includes('<INJECT:')) {
          const injectedParts = part.split(/<INJECT:([a-zA-Z0-9_-]+)\/>/g);
          const children = injectedParts.map((injectedPart, i) => {
            if (i % 2 === 0) {
              const nextInjectKey = i + 1 < injectedParts.length ? injectedParts[i + 1] : null;
              const hasFollowingInjectable = typeof nextInjectKey === 'string' && !!mergedInjectableComponents[nextInjectKey];
              const normalizedPart = hasFollowingInjectable
                ? injectedPart.replace(/(?:<div class="mb-3"><\/div>\s*)+$/g, '')
                : injectedPart;
              if (!hasMeaningfulHtml(normalizedPart)) return null;
              const instrumentedPart = instrumentTextTargets
                ? instrumentMarkdownTextTokens(normalizedPart)
                : normalizedPart;
              const markdownPartClassName = hasFollowingInjectable
                ? 'formatted-markdown-part pb-1 mb-3'
                : 'formatted-markdown-part';
              return React.createElement('div', { key: i, className: markdownPartClassName, dangerouslySetInnerHTML: { __html: instrumentedPart } });
            } else {
              const component = mergedInjectableComponents[injectedPart];
              if (!component) return null;

              // Add 8px spacing around injectables only when adjacent content exists,
              // so terminal injectables don't leave extra bottom dead space.
              const previousHtmlPart = injectedParts[i - 1] ?? '';
              const nextHtmlPart = injectedParts[i + 1] ?? '';
              const previousInjectKey = i >= 2 ? injectedParts[i - 2] : null;
              const nextInjectKey = i + 2 < injectedParts.length ? injectedParts[i + 2] : null;

              const hasPreviousContent = hasMeaningfulHtml(previousHtmlPart)
                || (typeof previousInjectKey === 'string' && !!mergedInjectableComponents[previousInjectKey]);
              const hasNextContent = hasMeaningfulHtml(nextHtmlPart)
                || (typeof nextInjectKey === 'string' && !!mergedInjectableComponents[nextInjectKey]);
              const nextStartsWithHeading = /^(?:\s*<div class="mb-3"><\/div>\s*)*<h[1-6]\b/i.test(nextHtmlPart.trim());

              const topSpacingClass = hasPreviousContent
                ? getInjectableTopSpacingClass(injectedPart)
                : '';

              const spacingClasses = [
                topSpacingClass,
                hasNextContent ? (nextStartsWithHeading ? 'pb-1' : 'mb-2') : '',
              ].filter(Boolean).join(' ');

              return React.createElement('div', { key: i, className: spacingClasses }, component);
            }
          });
          return React.createElement('div', { key: index, className: containerClassName }, ...children);
        }

        // Table parts are rendered full-width
        if (!hasMeaningfulHtml(part)) return null;
        const instrumentedPart = instrumentTextTargets ? instrumentMarkdownTextTokens(part) : part;
        return React.createElement('div', {
          key: index,
          className: `${containerClassName} formatted-markdown-part`,
          dangerouslySetInnerHTML: { __html: instrumentedPart }
        });
      })
    )
  );
};
